import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import AnimatedCounter from '../shared/AnimatedCounter';
import { FloatingParticles } from '../shared/FloatingElements';
import IntroOverlay from '../shared/IntroOverlay';
import ShapeGridCanvas from '../shared/ShapeGridCanvas';
import FlickerReveal from '../shared/FlickerReveal';
import ScrambleWord from '../shared/ScrambleWord';
import MagneticButton from '../shared/MagneticButton';
import TiltedAppPreview from '../shared/TiltedAppPreview';
import AuroraText from '../shared/AuroraText';
import BeamButton from '../shared/BeamButton';
import WordReveal from '../shared/WordReveal';
import dashboardCarbon from '../../../../public/ESGCaffe - Carbon Accounting.png';
import dashboardEnergy from '../../../../public/ESGCaffe - Energy Management.png';
import dashboardKpi from '../../../../public/ESGCaffe - KPI Cards.png';
import dashboardOrg from '../../../../public/ESGCaffe - Org Details.png';

const SCREENS = [
  { src: dashboardCarbon, alt: 'Carbon Accounting dashboard' },
  { src: dashboardEnergy, alt: 'Energy Management dashboard' },
  { src: dashboardKpi,    alt: 'KPI Cards dashboard' },
  { src: dashboardOrg,    alt: 'Organization Details' },
];
const CAPTIONS = ['Carbon Accounting', 'Energy Management', 'KPI Cards', 'Organization'];

/**
 * ESGCaffe hero — original content/palette (light mode only) with Breww-style
 * animation stack layered in:
 *   - IntroOverlay: full-screen leaf-logo balloon-out on first visit
 *   - ShapeGridCanvas: mouse-reactive hex canvas (blue borders, emerald hover)
 *   - FlickerReveal: tubelight flicker on the accent gradient phrase
 *   - ScrambleWord: cycles synonyms in the accent phrase
 *   - MagneticButton: subtle pointer attraction on both CTAs
 *   - Coordinated entrance from +90px, gated on `esg:intro-done`
 */

const getSaasLoginUrl = () => {
  const envUrl = import.meta.env.VITE_SAAS_URL;
  if (envUrl) return `${envUrl}/login`;
  const { protocol, hostname, port } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//saas.localhost${port ? `:${port}` : ''}/login`;
  }
  return `${protocol}//saas.esg.datacaffe.ai/login`;
};

const ACCENT_WORDS = ['Competitive Advantage', 'Strategic Insight', 'Measurable Impact', 'Automated Reporting', 'Audit-Ready Data'];

const stats = [
  { value: 15, suffix: '+', label: 'Companies' },
  { value: 800, suffix: '+', label: 'Hours Saved' },
  { value: 750, prefix: '$', suffix: 'K+', label: 'Penalties Avoided' },
  { value: 99.8, suffix: '%', label: 'Accuracy' },
];

const stagger = {
  parent: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.14, delayChildren: 0.1 },
    },
  },
  child: {
    hidden: { opacity: 0, y: 32 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  },
};

const floatingDashboard = {
  hidden: { opacity: 0, y: 80, scale: 0.92, rotateX: 8 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    rotateX: 0,
    transition: { duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

const HeroSection = () => {
  const [ready, setReady] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (sessionStorage.getItem('esg:intro-played') === '1') {
        setReady(true);
        return;
      }
    } catch (_) { /* ignore */ }
    const onDone = () => setReady(true);
    window.addEventListener('esg:intro-done', onDone);
    // Belt-and-suspenders — never leave the hero hidden
    const fb = window.setTimeout(() => setReady(true), 4200);
    return () => {
      window.removeEventListener('esg:intro-done', onDone);
      window.clearTimeout(fb);
    };
  }, []);

  // Scroll-linked parallax: as user scrolls through the hero, layers drift
  // apart to give a sense of depth (foreground rises, background sinks).
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '-14%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6, 0.9], [1, 0.6, 0]);
  const previewY = useTransform(scrollYProgress, [0, 1], ['0%', '-25%']);
  // Screen index — drives which of the 4 dashboards is visible. Advances
  // as user scrolls through the hero; auto-cycles when at rest.
  const [screenIdx, setScreenIdx] = useState(0);
  useEffect(() => {
    const unsub = scrollYProgress.on('change', (v) => {
      const idx = Math.min(SCREENS.length - 1, Math.floor(v * SCREENS.length));
      setScreenIdx((prev) => (prev === idx ? prev : idx));
    });
    return () => unsub();
  }, [scrollYProgress]);
  // Auto-advance while the user is idle at the top so the carousel plays.
  useEffect(() => {
    if (screenIdx !== 0) return;
    const id = window.setInterval(() => {
      if (window.scrollY < 40) setScreenIdx((i) => (i + 1) % SCREENS.length);
    }, 3500);
    return () => window.clearInterval(id);
  }, [screenIdx]);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <IntroOverlay />

      {/* ---------- Mouse-reactive hex grid (behind everything) ---------- */}
      <ShapeGridCanvas
        shape="hexagon"
        direction="diagonal"
        speed={0.28}
        squareSize={38}
        borderColor="rgba(59,130,246,0.14)"
        hoverFillColor="rgba(16,185,129,0.32)"
        hoverTrailAmount={5}
        className="z-[1]"
      />

      {/* ---------- Radial glow accents (scroll-parallax + ambient float) ---------- */}
      <motion.div
        className="pointer-events-none absolute inset-0 z-[2]"
        style={{ y: bgY, scale: bgScale }}
      >
        <motion.div
          className="pointer-events-none absolute top-[-20%] left-[10%] w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)', opacity: 0.5 }}
          animate={{ y: [0, -40, 10, -30, 0], x: [0, 20, -10, 15, 0], scale: [1, 1.15, 1.05, 1.12, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="pointer-events-none absolute bottom-[10%] right-[5%] w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.10) 0%, transparent 70%)', opacity: 0.4 }}
          animate={{ y: [0, 35, -15, 25, 0], x: [0, -25, 10, -18, 0], scale: [1, 1.1, 0.98, 1.08, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        />
        <motion.div
          className="pointer-events-none absolute top-[30%] right-[25%] w-[400px] h-[400px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)', opacity: 0.3 }}
          animate={{ y: [0, -25, 15, -20, 0], x: [0, 15, -20, 10, 0], scale: [1, 1.12, 1, 1.08, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 5 }}
        />
      </motion.div>

      {/* Floating particles */}
      <FloatingParticles count={25} color="#3b82f6" theme="light" />

      {/* ---------- Main content ---------- */}
      <motion.div
        className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pt-8 pb-8 md:pt-12 md:pb-12"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <motion.div
          className="max-w-5xl w-full text-center flex flex-col items-center"
          variants={stagger.parent}
          initial="hidden"
          animate={ready ? 'visible' : 'hidden'}
        >
          {/* Pill badge — with animated conic-gradient border via BeamButton */}
          <motion.div variants={stagger.child}>
            <BeamButton className="rounded-full">
              <span className="inline-flex items-center gap-2.5 rounded-full px-5 py-2 border border-blue-200/60 bg-white shadow-sm">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
                </span>
                <Sparkles className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-xs font-semibold tracking-wide uppercase text-slate-600">
                  AI-Powered ESG Compliance
                </span>
              </span>
            </BeamButton>
          </motion.div>

          {/* Headline — word-by-word reveal + aurora shimmer on the scramble */}
          <motion.h1
            variants={stagger.child}
            className="mt-8 md:mt-10 font-extrabold tracking-tight leading-[1.08] text-slate-900"
            style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}
          >
            <WordReveal run={ready} delay={0.1} stagger={0.09} text="Turn ESG Compliance Into" />{' '}
            <FlickerReveal delay={ready ? 0.75 : 0} run={ready}>
              <AuroraText className="font-extrabold">
                <ScrambleWord words={ACCENT_WORDS} holdMs={2800} scrambleMs={1000} minWidth="14ch" />
              </AuroraText>
            </FlickerReveal>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={stagger.child}
            className="mt-6 md:mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-slate-500"
          >
            The AI-powered platform that automates compliance reports, tracks
            carbon emissions, and simplifies sustainability analytics
            &mdash;&nbsp;in minutes, not months.
          </motion.p>

          {/* CTAs — magnetic pointer attraction */}
          <motion.div
            variants={stagger.child}
            className="mt-10 flex flex-col sm:flex-row items-center gap-4"
          >
            <MagneticButton strength={0.28}>
              <BeamButton className="rounded-md">
                <a href={getSaasLoginUrl()}>
                  <button className="group relative inline-flex items-center gap-2.5 rounded-md px-8 py-3.5 text-sm font-bold transition-all duration-300 bg-blue-500 text-white shadow-sm hover:bg-blue-600">
                    Start Free Trial
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </button>
                </a>
              </BeamButton>
            </MagneticButton>
            <MagneticButton strength={0.22}>
              <a
                href="https://meetings-na2.hubspot.com/datacaffe-ai/esgcaffe-discovery-call"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="group inline-flex items-center gap-2.5 rounded-md border border-slate-200 bg-white px-8 py-3.5 text-sm font-semibold text-slate-700 shadow-sm backdrop-blur-sm transition-all duration-300 hover:bg-slate-50">
                  Book a Demo
                  <ArrowRight className="h-4 w-4 opacity-70 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100" />
                </button>
              </a>
            </MagneticButton>
          </motion.div>

          {/* Social proof */}
          <motion.p
            variants={stagger.child}
            className="mt-6 text-xs font-medium tracking-wide text-slate-400"
          >
            Trusted by 15+ Companies &middot; No credit card required
          </motion.p>
        </motion.div>

        {/* ---------- 3D-tilted dashboard mockup ("One window" pattern) ---------- */}
        <motion.div
          className="relative mt-14 md:mt-20 w-full max-w-5xl"
          variants={floatingDashboard}
          initial="hidden"
          animate={ready ? 'visible' : 'hidden'}
          style={{ y: previewY, willChange: 'transform' }}
        >
          <TiltedAppPreview
            url="app.esgcaffe.ai/dashboard"
            baseRotateX={10}
            baseRotateY={-8}
            screens={SCREENS}
            captions={CAPTIONS}
            activeIndex={screenIdx}
          />
          <div className="mt-6 text-center text-xs uppercase tracking-[0.3em] text-slate-400">
            Scroll to navigate ↓
          </div>
        </motion.div>

        {/* ---------- Stats row ---------- */}
        <motion.div
          className="mt-14 md:mt-20 w-full max-w-4xl"
          initial={{ opacity: 0, y: 30 }}
          animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 rounded-2xl border border-slate-200/60 bg-white/70 px-6 py-8 md:px-10 md:py-10 shadow-lg shadow-slate-200/30 backdrop-blur-sm">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
                  <AnimatedCounter
                    target={stat.value}
                    prefix={stat.prefix || ''}
                    suffix={stat.suffix}
                    duration={2200}
                  />
                </div>
                <div className="mt-1.5 text-xs font-medium uppercase tracking-widest text-slate-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
